import { z } from "zod";
import { stageSessionLanguageZod } from "./languages.zod.js";
import { zString } from "./zod_utils.js";
/**
 * Voices — what a translation is spoken in.
 *
 * Voicely's synthesis vendor offers a roster of a few dozen voices, every one
 * of which speaks every language. ConferHall offers the whole roster, read
 * from voicely, and adds two things of its own: a sample clip per voice per
 * language (synthesised once through voicely and kept on our storage, so a
 * voice can be heard before it is chosen without a vendor request per
 * listen) and a default voice per gender, chosen by a super-admin, which is
 * what a session that names no voice is spoken in.
 *
 * A session names two voices: `hallVoiceId`, what the audience hears the
 * speaker in, and `stageVoiceId`, what the speaker hears the floor in. The
 * floor's voice can follow the gender of whoever is at the floor mic
 * (`matchFloorGender`: voicely switches to the voice's opposite-gender
 * counterpart after a few sentences); the speaker's cannot yet — the
 * runtime measures pitch on the floor leg only.
 */
export const voiceGenderZod = z.enum(["female", "male"]);
/**
 * A voice's pace, 1 = natural: what voicely's speech engine is asked for
 * (`hall_voice_speed` / `stage_voice_speed`). Voicely takes 0.85–1.5, but
 * its live-translation engine (Soniox) plays anything past 1.3 at 1.3, so
 * the top of a slider beyond it would do nothing. Sent only when the
 * deployed voicely takes it (the catalog's `paceAvailable`).
 */
export const VOICE_SPEED_MIN = 0.85;
export const VOICE_SPEED_MAX = 1.3;
export const voiceSpeedZod = z.number().min(VOICE_SPEED_MIN).max(VOICE_SPEED_MAX);
/**
 * A voice as voicely's roster describes it, straight from the vendor, plus
 * `languages`: the ISO codes the voice's accent is tuned for. Every voice
 * speaks every language; a Russian hall still wants a voice recorded on
 * Slavic speech, and a picker leads with those.
 */
export const voicelyVoiceZod = z.object({
    id: z.string(),
    gender: voiceGenderZod.nullable(),
    accent: z.string().nullable(),
    age: z.string().nullable(),
    description: z.string().nullable(),
    languages: z.array(z.string()),
});
export const voicelyVoiceListZod = z.object({
    voices: z.array(voicelyVoiceZod),
    /** What voicely speaks in when a session names no voice. */
    defaultVoiceId: z.string(),
});
/** Sample clips by language code, as URLs on our storage. */
export const voiceSamplesZod = z.record(z.string(), z.url().max(2048));
/** A voice as ConferHall offers it: voicely's description plus our clips. */
export const voiceZod = voicelyVoiceZod.extend({ samples: voiceSamplesZod });
/** The super-admin's default per gender; null = voicely's own default. */
export const voiceDefaultsZod = z.object({
    female: z.string().nullable(),
    male: z.string().nullable(),
});
/** Defaults per language, ahead of the global pair; only languages with one set appear. */
export const languageVoiceDefaultsZod = z.record(z.string(), voiceDefaultsZod);
/**
 * The default voice for a language and gender: the language's own, else
 * the global one, else null (voicely's own default applies).
 */
export const defaultVoiceFor = (defaults, languageDefaults, language, gender) => languageDefaults[language]?.[gender] ?? defaults[gender] ?? null;
/** `sessionDefaultVoice`'s steps 1–3 for one gender. */
const defaultOfGender = (catalog, language, gender) => {
    const own = catalog.languageDefaults[language]?.[gender];
    if (own)
        return own;
    const tuned = catalog.voices
        .filter((v) => v.gender === gender && v.languages.includes(language))
        .sort((a, b) => a.languages.length - b.languages.length)[0];
    return tuned?.id ?? catalog.defaults[gender] ?? null;
};
export const sessionDefaultVoice = (catalog, language) => defaultOfGender(catalog, language, "male") ?? defaultOfGender(catalog, language, "female");
/**
 * The voice to carry over when the language a voice speaks changes: the
 * new language's default in the gender of the voice that was speaking — a
 * man who picked a man's voice keeps one — by `sessionDefaultVoice`'s
 * steps, falling back to the other gender and then to null (voicely's own
 * default) where the language has none. With no previous voice, or one
 * the roster does not know, it is the language's session default.
 */
export const followVoice = (catalog, previousVoiceId, language) => {
    const gender = catalog.voices.find((v) => v.id === previousVoiceId)?.gender;
    if (!gender)
        return sessionDefaultVoice(catalog, language);
    return (defaultOfGender(catalog, language, gender) ??
        defaultOfGender(catalog, language, gender === "male" ? "female" : "male"));
};
/** The voices tuned for a language: those whose accent suits it. */
export const voicesTunedFor = (voices, language) => voices.filter((v) => v.languages.includes(language));
/** Everything a picker needs: the roster, our clips, the defaults. */
export const voiceCatalogZod = z.object({
    voices: z.array(voiceZod),
    defaults: voiceDefaultsZod,
    languageDefaults: languageVoiceDefaultsZod,
    /** What voicely speaks in when a session names no voice and no default is set. */
    defaultVoiceId: z.string(),
    /**
     * Voicely could not be asked and nothing was cached: `voices` is empty
     * and a picker should say so rather than offer nothing silently.
     */
    rosterUnavailable: z.boolean(),
    /** The deployed voicely takes a voice's pace: pickers offer it only then. */
    paceAvailable: z.boolean(),
});
export const setDefaultVoiceInputZod = z.object({
    gender: voiceGenderZod,
    /** Null clears the default; a session then gets voicely's own. */
    voiceId: zString.min(1).max(64).nullable(),
    /** A language's own default, ahead of the global one; null = the global default. */
    language: stageSessionLanguageZod.nullable().default(null),
});
/** Hear a voice in a language: the clip is made on first ask and kept. */
export const voiceSampleRequestZod = z.object({
    voiceId: zString.min(1).max(64),
    language: stageSessionLanguageZod,
});
/** The super-admin's version may also throw a clip away and make it afresh. */
export const voiceSampleInputZod = voiceSampleRequestZod.extend({
    regenerate: z.boolean().default(false),
});
export const voiceSampleZod = z.object({
    voiceId: z.string(),
    language: z.string(),
    url: z.url().max(2048),
});
//# sourceMappingURL=voice.zod.js.map